<script setup lang="ts">
import type { FiltersSidebarProps, PropertyFilterGroup } from '.';
import type { PropertyFilters } from '~/core/entities/property/filters';

import { useEventDataFilters } from '~/composables/events/useEventDataFilters/useEventDataFilters';
import { useEvents } from '~/composables/events/useEvents';
import { useAddresses } from '~/composables/useAddressesWrapper';
import { INITIAL_FILTERS } from '~/composables/usePropertiesListingFilters';
import { usePropertyResponsiveFilters } from '~/composables/usePropertyResponsiveFilters/usePropertyResponsiveFilters';
import { MixpanelProps } from '~/core/service/mixpanel';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';
import { grammarInflection, isNumber } from '~~/utils';

import { FiltersAccordion } from '~/components/Filters/FiltersAccordion';

import CloseIcon from '~icons/material-symbols/close';
import DoneIcon from '~icons/material-symbols/done';

import messages from './FiltersSidebar.locale.json';
import { getSidebarStyles } from './variants';

// Pré-carrega os dados de endereços para evitar delay no primeiro clique do accordion
// O composable usa useAsyncData com server: false, então só executa no cliente
useAddresses();

const { searchPropertyEvents } = useEvents();
const { getFilterClearedEventStatus } = useEventDataFilters();

const strings = messages.pt;

interface Emits {
	(e: 'clearFilters'): void;
	(e: 'applyFilters', value: PropertyFilters): void;
	(e: 'retrySelectedAgent'): void;
}

const props = withDefaults(defineProps<FiltersSidebarProps>(), {
	variant: 'primary',
	useExternalCount: false,
	trackEvents: true,
	showRangeActions: false,
	preserveDraftsOnSync: false
});
const emit = defineEmits<Emits>();

const styles = computed(() => getSidebarStyles(props.variant));

const isActive = defineModel<boolean>('isActive', {
	default: false
});

const sidebar = shallowRef<HTMLDivElement | null>(null);

onClickOutside(sidebar, () => (isActive.value = false));

const { filters: localFilters } = usePropertiesListingFilters();

const propertyCount = ref(props.countProperties);
const isGettingPropertyCount = ref(false);
const { user } = useUserSession();

async function handleGetPropertyCount() {
	if (!isActive.value) return;

	if (props.useExternalCount) {
		propertyCount.value = props.countProperties;
		return;
	}

	isGettingPropertyCount.value = true;
	try {
		const [result, error] = await nuxtPropertiesRepository.list({
			page: 1,
			perPage: 1,
			...localFilters.value,
			markUserWishlistsId: user.value?.id
		});

		if (error) {
			console.error(error);
			propertyCount.value = 0;
			return;
		}

		propertyCount.value = result?.pagination.filteredCount || 0;
	} finally {
		isGettingPropertyCount.value = false;
	}
}

watch(
	() => props.countProperties,
	newCount => {
		if (!isActive.value || props.useExternalCount) {
			propertyCount.value = newCount;
		}
	}
);

watch(
	() => props.isFetchingProperties,
	value => {
		if (props.useExternalCount) {
			isGettingPropertyCount.value = !!value;
		}
	}
);

watchDebounced(
	localFilters,
	() => {
		if (isActive.value) {
			handleGetPropertyCount();
		}
	},
	{
		deep: true,
		debounce: 300
	}
);

let lastSyncedFilters: PropertyFilters = {};
const preservedDraftKeys = new Set<keyof PropertyFilters>();

watch(
	[isActive, () => props.preserveDraftsOnSync],
	([isOpen, preserveDrafts]) => {
		if (!isOpen || !preserveDrafts) preservedDraftKeys.clear();
	},
	{ flush: 'sync' }
);

function syncFilters() {
	const filtersCopy: PropertyFilters = JSON.parse(
		JSON.stringify(props.filters)
	);

	if (props.preserveDraftsOnSync && isActive.value) {
		// Only used during an automatic invalid-agent cleanup. Keep edits made
		// since the last sync, including cleared fields, while updating untouched ones.
		const draft: PropertyFilters = JSON.parse(
			JSON.stringify(localFilters.value)
		);
		const keys = new Set<keyof PropertyFilters>([
			...Object.keys(lastSyncedFilters),
			...Object.keys(draft)
		] as (keyof PropertyFilters)[]);
		for (const key of keys) {
			if (
				JSON.stringify(draft[key]) !== JSON.stringify(lastSyncedFilters[key])
			) {
				preservedDraftKeys.add(key);
			}
		}
		function preserveDraftValue<Key extends keyof PropertyFilters>(key: Key) {
			if (key in draft) filtersCopy[key] = draft[key];
			else delete filtersCopy[key];
		}
		preservedDraftKeys.forEach(preserveDraftValue);
	} else {
		preservedDraftKeys.clear();
	}

	lastSyncedFilters = JSON.parse(JSON.stringify(props.filters));
	localFilters.value = filtersCopy;

	if (isActive.value) {
		handleGetPropertyCount();
	}
}

watch(
	() => props.filters,
	() => {
		if (isActive.value) {
			syncFilters();
		}
	},
	{ deep: true, immediate: true }
);

watch(isActive, newValue => {
	if (newValue === true) {
		syncFilters();
	}
});

function removeAddressFilter(filters: PropertyFilters) {
	filters.addressSlug = '';
	filters.poiLocationLat = undefined;
	filters.poiLocationLng = undefined;
}

async function updateFilters(filters: PropertyFilters) {
	const newFilters = { ...filters };

	if (newFilters.regions && newFilters.regions.length > 0) {
		removeAddressFilter(newFilters);
	}

	const keys = Object.keys(newFilters) as (keyof PropertyFilters)[];

	keys.forEach(key => {
		const value = newFilters[key];

		if (value == null || (!value && typeof value === 'number')) {
			delete newFilters[key];
			return;
		}
	});

	if (newFilters.commercialIds) {
		delete newFilters.commercialIds;
	}

	localFilters.value = newFilters;
}

async function waitForFetchingToComplete() {
	return await until(() => !props.isFetchingProperties).toBeTruthy();
}

const waitForTotalResults = async () => {
	//Forcing update total results considering 0 as a valid value
	return await until(() => isNumber(props.countProperties)).toBeTruthy();
};

async function handleClearFilter() {
	const localFiltersCopy = { ...localFilters.value } as PropertyFilters;
	await until(() => props.isFetchingProperties).toBeTruthy();
	await Promise.all([waitForFetchingToComplete(), waitForTotalResults()]);

	if (props.trackEvents) {
		handleEventsClearFilter(localFiltersCopy);
	}
}

async function clearFilters() {
	handleClearFilter();
	updateFilters(INITIAL_FILTERS);
	emit('clearFilters');
}

const handleEventsClearFilter = (localFiltersCopy: PropertyFilters) => {
	const clearedFiltersInfosForEvents = getFilterClearedEventStatus(
		localFiltersCopy,
		propertyCount.value
	);

	clearedFiltersInfosForEvents[MixpanelProps.TOTAL_RESULTS] =
		props.countProperties;

	searchPropertyEvents.trackFiltersCleared(clearedFiltersInfosForEvents);
};

const { filterComponents: responsiveFilterComponents } =
	usePropertyResponsiveFilters({
		filters: computed(() => localFilters.value),
		updateFilters,
		pageName: props.pageName,
		showRangeActions: props.showRangeActions,
		selectedAgent: toRef(props, 'selectedAgent'),
		selectedAgentResolution: toRef(props, 'selectedAgentResolution'),
		retrySelectedAgent: () => emit('retrySelectedAgent')
	});

const resolvedFilterComponents = computed<PropertyFilterGroup[]>(() => {
	if (props.filterComponents?.length) {
		return props.filterComponents;
	}
	return responsiveFilterComponents.value;
});

const openFilterComponents = computed<PropertyFilterGroup[]>(() => {
	if (!resolvedFilterComponents.value.length) return [];

	return resolvedFilterComponents.value.filter(({ isOpen }) => isOpen);
});

const moreFilterComponents = computed<PropertyFilterGroup[]>(() => {
	if (!resolvedFilterComponents.value.length) return [];

	return resolvedFilterComponents.value.filter(({ isOpen }) => !isOpen);
});

const applyFiltersButtonText = computed<string>(() => {
	if (props.applyButtonText) {
		return props.applyButtonText;
	}

	if (propertyCount.value === 0) return strings.applyFiltersButton.empty;

	return strings.applyFiltersButton.default
		.replace('{propertyCount}', String(propertyCount.value))
		.replace(
			'{propertiesText}',
			String(
				grammarInflection(
					propertyCount.value,
					strings.applyFiltersButton.singular,
					strings.applyFiltersButton.plural
				)
			)
		);
});

async function handleApplyFilters() {
	emit('applyFilters', localFilters.value);
}
</script>

<template>
	<aside ref="sidebar" :class="[styles.aside, { 'translate-x-0': isActive }]">
		<div class="relative h-full pb-[4.25rem] md:pb-24">
			<div class="flex h-full flex-col">
				<header :class="styles.header">
					<span :class="styles.headerTitle">
						{{ strings.header }}
					</span>
					<button
						class="flex size-8 items-center justify-center"
						type="button"
						@click="isActive = false"
					>
						<CloseIcon :class="styles.closeIcon" />
					</button>
				</header>

				<div class="flex h-full flex-col overflow-y-auto">
					<FiltersAccordion
						v-if="openFilterComponents.length"
						:key="`open-filters-${isActive}`"
						title="Filtros abertos"
						:variant="props.variant"
					>
						<template
							v-for="(filter, index) in openFilterComponents"
							:key="`open-filter-${filter.id}`"
						>
							<div class="flex items-center justify-center px-5 pb-6 pt-5">
								<component
									:is="filter.component"
									:value="filter.id"
									v-bind="{ ...filter.extraProps, variant: props.variant }"
									class="w-full"
									@update:model-value="filter.onUpdate"
								/>
							</div>
							<hr
								v-if="index < openFilterComponents.length - 1"
								:class="styles.separator"
							/>
						</template>
					</FiltersAccordion>

					<FiltersAccordion
						v-if="moreFilterComponents.length"
						:key="`more-filters-${isActive}`"
						title="Mais filtros"
						trigger-class="border-t"
						:initialize-open="true"
						:variant="props.variant"
					>
						<template
							v-for="(filter, index) in moreFilterComponents"
							:key="`extra-filter-${filter.id}`"
						>
							<div class="flex items-center justify-center px-5 pb-6 pt-5">
								<component
									:is="filter.component"
									v-bind="{ ...filter.extraProps, variant: props.variant }"
									:value="filter.id"
									class="w-full"
									@update:model-value="filter.onUpdate"
								/>
							</div>
							<hr
								v-if="index < moreFilterComponents.length - 1"
								:class="styles.separator"
							/>
						</template>
					</FiltersAccordion>
				</div>
			</div>

			<footer v-show="isActive" :class="styles.footer">
				<Button
					type="button"
					:class="styles.clearButton"
					variant="transparent"
					@click="clearFilters"
				>
					{{ strings.clearFiltersButton }}
				</Button>
				<Button
					type="button"
					class="w-min min-w-[200px] text-base font-medium"
					:variant="styles.applyButtonVariant"
					@click="handleApplyFilters"
				>
					<template v-if="isGettingPropertyCount">
						<IconPilarCircularProgress :class="styles.loadingIcon" />
					</template>
					<template v-else>
						{{ applyFiltersButtonText }}
						<DoneIcon class="size-5" />
					</template>
				</Button>
			</footer>
		</div>
	</aside>
</template>
